import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import CircularProgress from '@mui/material/CircularProgress';
import { visuallyHidden } from '@mui/utils';
import useMediaQuery from '@mui/material/useMediaQuery';
import { makeStyles } from '@mui/styles';
import { decodeJWT, getAllRefferedDetails } from '../../../services/api';

const ROWS_PER_PAGE = 10;

// Define the makeStyles hook
const useStyles = makeStyles((theme) => ({
  greenText: {
    color: `${theme.palette.success.main} !important`,
  },
  redText: {
    color: `${theme.palette.error.main} !important`,
  },
}));

// Only `name` and `date` are backed by real columns the server can sort on
// (referred user's email / join date). The order-derived columns are display
// only, so they are not offered as sort targets.
const headCells = [
  { id: 'type', numeric: false, disablePadding: true, label: 'Type', sortable: false },
  { id: 'name', numeric: true, disablePadding: false, label: 'Name', sortable: true },
  { id: 'date', numeric: true, disablePadding: false, label: 'Joined', sortable: true },
  { id: 'rank', numeric: true, disablePadding: false, label: 'Rank', sortable: false },
  { id: 'commission', numeric: true, disablePadding: false, label: 'Commission', sortable: false },
  {
    id: 'commission_percentage',
    numeric: true,
    disablePadding: false,
    label: 'Commission Percentage',
    sortable: false,
  },
  { id: 'orderTotal', numeric: true, disablePadding: false, label: 'Order Total', sortable: false },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort, isMobile } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  if (isMobile) {
    return null; // Don't render the table header on mobile devices
  }

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{ borderBottom: 'none !important' }}
          >
            {headCell.sortable ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

function mapReferredUser(user, index) {
  const orders = user.orders || [];
  const firstOrder = orders[0];
  const joined = user.joinedDate || (firstOrder && firstOrder.date);
  return {
    id: user.email || index + 1,
    type: 'User',
    name: user.email,
    date: joined ? new Date(joined).toLocaleDateString() : 'N/A',
    rank: 'N/A',
    commission: firstOrder ? firstOrder.commission || 0 : 0,
    commission_percentage: firstOrder ? firstOrder.commission_percentage || 0 : 0,
    orderTotal: firstOrder ? firstOrder.total || 0 : 0,
  };
}

export default function EnhancedTable() {
  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState('date');
  const [page, setPage] = React.useState(0);
  const [rows, setRows] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    async function fetchReferredUsers() {
      setLoading(true);
      try {
        const access_token = String(localStorage.getItem('access_token'));
        const decoded = decodeJWT(access_token);
        const response = await getAllRefferedDetails(decoded.email, {
          page: page + 1,
          limit: ROWS_PER_PAGE,
          sortBy: orderBy,
          sortDir: order,
        });
        if (cancelled) return;
        const data = response && response.data;
        if (data && Array.isArray(data.referredUsers)) {
          setRows(data.referredUsers.map(mapReferredUser));
          setTotal((data.pagination && data.pagination.total) || data.referredUsers.length);
          setError(null);
        } else {
          setRows([]);
          setTotal(0);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching referred users:', err);
        setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchReferredUsers();
    return () => {
      cancelled = true;
    };
  }, [page, order, orderBy]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <TableContainer>
        <Table
          sx={{ minWidth: { xs: '100%', sm: 750 } }}
          aria-labelledby="tableTitle"
          size="medium"
        >
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            isMobile={isMobile}
          />
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length > 0 ? (
              rows.map((row) => (
                <TableRow role="checkbox" tabIndex={-1} key={row.id}>
                  <TableCell
                    component="th"
                    scope="row"
                    padding="none"
                    sx={{ borderBottom: 'none !important' }}
                  >
                    {row.type}
                  </TableCell>
                  <TableCell align="right" sx={{ borderBottom: 'none !important' }}>
                    {row.name}
                  </TableCell>
                  {!isMobile && (
                    <>
                      <TableCell align="right" sx={{ borderBottom: 'none !important' }}>
                        {row.date}
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: 'none !important' }}>
                        {row.rank}
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: 'none !important' }}>
                        {row.commission}
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: 'none !important' }}>
                        {row.commission_percentage}%
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: 'none !important' }}>
                        {row.orderTotal}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No referral users found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={ROWS_PER_PAGE}
        rowsPerPageOptions={[ROWS_PER_PAGE]}
      />
    </Box>
  );
}

EnhancedTable.propTypes = {
  searchQuery: PropTypes.string,
  hideAssets: PropTypes.bool,
};
